(ns hai.home.mobile.mini-apps.tasks
  "My Tasks mini-app card for the mobile dashboard: the same task data as the
   full Tasks page, grouped into collapsible Now / Next / Later sections. The ＋
   swaps the card into Add-a-task mode — the header retitles and the body
   becomes the title form ([[hai.tasks.form]]); the title links to the full
   Tasks page. Shares the task row with the desktop card via [[hai.tasks.card]]
   and the tinted header row via [[hai.tasks.header]]; the card frame stays
   mobile's own."
  (:require
   [hai.entity.data :as entity-data]
   [hai.home.mobile.mini-apps :as mini-apps]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.routing :as routing]
   [humbleai.belt.set :as set]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def ^:private close-icon (icons/mui-icon :outlined :close))

;; The frame stays mobile's own — softer shadow, 20px radius, touch-sized
;; targets — but the header row is the shared one every tasks surface uses.
(o/defstyled card mini-apps/card
  [header/bar
   {:border-top-left-radius "20px"
    :border-top-right-radius "20px"}
   ;; Touch targets, as with the row's edit/delete below.
   [:.bar-action-btn {:width "32px" :height "32px"}]]
  ;; Rows float as spaced cards rather than a bordered list, so the raised ones
  ;; read as pressable. The inset holds them off the section edges and gives the
  ;; drop shadows room.
  [:.section-tasks
   {:display "flex" :flex-direction "column" :gap "8px"
    :padding "4px 14px 16px"}]
  ;; The shared frame clips the last section to round the card's bottom; with
  ;; inset floating cards there is nothing to clip, and the clip would eat their
  ;; shadows. Let it overflow instead.
  [mini-apps/section
   [:&:last-child {:overflow "visible"}]]
  ;; Roomier edit/delete tap targets on touch (the shared row uses 26px for the
  ;; desktop pointer).
  [task-card/task-row
   [:.icon-btn {:width "32px" :height "32px"}]])

(defn- section-view [{:keys [label tasks open? on-toggle]}]
  [mini-apps/section-view
   {:label label :item-count (count tasks) :open? open? :on-toggle on-toggle}
   (when (seq tasks)
     (into [:div.section-tasks]
           (map (fn [task]
                  ^{:key (:task/uuid task)}
                  [task-card/task-row-component task
                   {:hover-actions? false
                    :show-hint? false
                    :on-open-entity entity-data/open-entity}]))
           tasks))])

(defn panel []
  (r/with-let [!adding? (r/atom false)
               !open (r/atom #{:now})
               toggle-section (fn [id] (swap! !open set/toggle-membership id))]
    (let [buckets @(<s [::data/buckets])
          loaded? @(<s [::data/loaded?])
          adding? @!adding?
          open @!open
          total (reduce + (map count (vals buckets)))]
      (if adding?
        [card
         [header/bar {:title "Add a task"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Cancel"
            :title "Cancel"
            :on-click #(reset! !adding? false)}
           [close-icon {:size 18}]]]
         [form/task-form {:on-save (fn [staged]
                                     (>e [::data/create staged])
                                     (swap! !open conj (data/staged-bucket staged)))
                          :on-close #(reset! !adding? false)
                          :show-hint? false
                          :render-assignee-picker
                          (fn [{:keys [on-pick trigger]}]
                            [assignee-picker/component
                             {:editable? true
                              :on-pick on-pick
                              :label "Assign"
                              :trigger trigger}])}]]
        [card
         [header/bar
          {:title "My Tasks"
           :href (routing/href :tasks/view)
           :target routing/link-target}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Add a task"
            :title "Add a task"
            :on-click #(reset! !adding? true)}
           [icons/plus {:size 16}]]]
         (cond
           (not loaded?) nil
           (zero? total) [:div.app-placeholder "Nothing yet — add your first task above."]
           :else
           (into [:div.task-sections]
                 (map (fn [{:keys [id label]}]
                        ^{:key id}
                        [section-view {:label label
                                       :tasks (get buckets id)
                                       :open? (contains? open id)
                                       :on-toggle #(toggle-section id)}]))
                 data/bucket-tabs))]))))
